import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Zap, Battery, Clock, Hash, Calendar, TrendingUp, Phone, Loader2, IndianRupee } from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Z_INDEX } from '../../constants';
import { formatDuration } from '../../utils/transformers';
import { useChargePointStatus } from '../../hooks/useChargePointStatus';
import { toast } from 'sonner';
import { transactionService } from '../../services/transactionService';
import { useActiveChargingStore } from '../../store/activeChargingStore';
import { mapTariffToDisplay } from '../../utils/tariff';
import { Tariff } from '../../types';
import { SUPPORT_TEL } from '../../lib/support';

interface ChargingStatusPageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStopCharging?: () => void;
  onTransactionStopped?: (transactionId: string) => void;
  transactionId?: string;
  chargePointId?: string;
  connectorId?: string;
  isStoppingCharge?: boolean;
}

interface TransactionData {
  currentSoc?: number;
  currentPowerKw?: number;
  totalKwh?: number;
  totalCost?: number;
  meterStart?: number;
  startedAt?: string;
  lastUpdatedAt?: string;
  tariffs?: Tariff[];
}

const LIVE_STATUS_POLL_MS = 10_000;

export function ChargingStatusPage({
  open,
  onOpenChange,
  onStopCharging,
  onTransactionStopped,
  transactionId,
  chargePointId,
  connectorId,
  isStoppingCharge = false,
}: ChargingStatusPageProps) {
  // Local state
  const [duration, setDuration] = useState(0);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; delay: number }>>([]);

  // Real transaction data from SSE
  const [transactionData, setTransactionData] = useState<TransactionData>({});
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  // Ref to hold latest callback (avoids stale closure in SSE handlers)
  const onTransactionStoppedRef = useRef(onTransactionStopped);

  // Keep ref in sync with prop
  useEffect(() => {
    onTransactionStoppedRef.current = onTransactionStopped;
  }, [onTransactionStopped]);

  // Resolved IDs — seeded from props (in-app navigation), then hydrated from
  // durable sources (active session store, transaction fetch) so that refresh,
  // direct URL, and notification-tap cases still subscribe to the right channels.
  const [resolvedCpId, setResolvedCpId] = useState<string | undefined>(chargePointId);
  const [resolvedConnId, setResolvedConnId] = useState<string | undefined>(connectorId);

  // Hydrate from active charging store when this page matches the active session
  const activeSessionTxId = useActiveChargingStore((s) => s.transactionId);
  const activeSessionCpId = useActiveChargingStore((s) => s.chargePointId);
  const activeSessionConnId = useActiveChargingStore((s) => s.connectorId);
  const activeSessionBatteryLevel = useActiveChargingStore((s) => s.batteryLevel);
  const activeSessionCurrentPower = useActiveChargingStore((s) => s.currentPower);
  const activeSessionEnergyConsumed = useActiveChargingStore((s) => s.energyConsumed);
  useEffect(() => {
    if (resolvedCpId && resolvedConnId) return;
    if (activeSessionTxId && activeSessionTxId === transactionId) {
      if (!resolvedCpId && activeSessionCpId) setResolvedCpId(activeSessionCpId);
      if (!resolvedConnId && activeSessionConnId) setResolvedConnId(activeSessionConnId);
    }
  }, [transactionId, activeSessionTxId, activeSessionCpId, activeSessionConnId, resolvedCpId, resolvedConnId]);

  useEffect(() => {
    if (!transactionId || activeSessionTxId !== transactionId) return;

    setTransactionData(prev => ({
      ...prev,
      currentSoc: prev.currentSoc ?? (activeSessionBatteryLevel > 0 ? activeSessionBatteryLevel : prev.currentSoc),
      currentPowerKw: prev.currentPowerKw ?? (activeSessionCurrentPower > 0 ? activeSessionCurrentPower : prev.currentPowerKw),
      totalKwh: prev.totalKwh ?? (activeSessionEnergyConsumed > 0 ? activeSessionEnergyConsumed : prev.totalKwh),
    }));
  }, [
    transactionId,
    activeSessionTxId,
    activeSessionBatteryLevel,
    activeSessionCurrentPower,
    activeSessionEnergyConsumed,
  ]);

  // Subscribe to charge point status for connector updates
  const { evseStatuses, isConnected: cpConnected } = useChargePointStatus({
    chargePointId: resolvedCpId ?? null,
    enabled: open && !!resolvedCpId,
  });

  // Get connector status from polling
  const connectorStatus = resolvedConnId ? evseStatuses.get(resolvedConnId)?.status : undefined;
  const connectorRawStatus = resolvedConnId ? evseStatuses.get(resolvedConnId)?.rawStatus : undefined;

  // Format raw OCPP status to user-friendly label
  const formatConnectorStatus = (rawStatus: string): string => {
    const normalized = rawStatus.toUpperCase();
    switch (normalized) {
      case 'CHARGING': return 'Charging';
      case 'FINISHING': return 'Finishing';
      case 'SUSPENDED_EV': return 'Suspended (EV)';
      case 'SUSPENDED_EVSE': return 'Suspended (EVSE)';
      case 'OCCUPIED': return 'Occupied';
      case 'PREPARING': return 'Preparing';
      case 'AVAILABLE': return 'Available';
      case 'FAULTED': return 'Faulted';
      case 'UNAVAILABLE': return 'Unavailable';
      default: return rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
    }
  };

  // Get color styling based on OCPP status
  const getConnectorStatusColor = (rawStatus: string): { dot: string; text: string } => {
    const normalized = rawStatus.toUpperCase();
    switch (normalized) {
      case 'CHARGING':
        return { dot: 'bg-green-500', text: 'text-green-400' };
      case 'FINISHING':
        return { dot: 'bg-blue-500', text: 'text-blue-400' };
      case 'SUSPENDED_EV':
      case 'SUSPENDED_EVSE':
        return { dot: 'bg-yellow-500', text: 'text-yellow-400' };
      case 'PREPARING':
        return { dot: 'bg-blue-500', text: 'text-blue-400' };
      case 'FAULTED':
      case 'UNAVAILABLE':
        return { dot: 'bg-red-500', text: 'text-red-400' };
      default:
        return { dot: 'bg-orange-500', text: 'text-orange-400' };
    }
  };

  const syncTransactionSnapshot = useCallback(async () => {
    if (!transactionId) return;

    try {
      const tx = await transactionService.getTransactionById(transactionId);

      const startTimeStr = tx.startConfirmedByEvseAt ?? tx.startRequestedAt ?? tx.createdAt;
      setSessionStartTime(new Date(startTimeStr));

      setResolvedCpId(prev => prev ?? tx.chargePointId);
      setResolvedConnId(prev => prev ?? tx.connectorId);
      // Tariff is attached by the backend (findOne) when the transaction has a
      // tariffId. Keep any previously-resolved tariff if a later poll omits it.
      const mappedTariffs = mapTariffToDisplay((tx as any).tariff);
      setTransactionData(prev => ({
        ...prev,
        currentSoc: tx.currentSoc ?? prev.currentSoc,
        currentPowerKw: tx.currentPowerKw ?? prev.currentPowerKw,
        totalKwh: tx.totalKwh ?? prev.totalKwh,
        meterStart: tx.meterStart ?? prev.meterStart,
        lastUpdatedAt: tx.lastMeterValueAt ?? tx.updatedAt ?? prev.lastUpdatedAt,
        tariffs: mappedTariffs.length > 0 ? mappedTariffs : prev.tariffs,
      }));

      // Only treat as stopped if EVSE confirmed start then stopped, or CSMS failed.
      // A brand-new transaction has isActive=false before EVSE confirms — that's pending, not stopped.
      const isTrulyStopped = !tx.isActive && (tx.stoppedAt || tx.csmsFailed);
      if (isTrulyStopped) {
        onTransactionStoppedRef.current?.(transactionId);
      }
    } catch {
      // Polling failure — will retry on next interval
    }
  }, [transactionId]);

  // Reset state when page opens with new transaction (must run before SSE subscription)
  useEffect(() => {
    if (open && transactionId) {
      setTransactionData({});
      setDuration(0);
      setSessionStartTime(null);
      setResolvedCpId(chargePointId);
      setResolvedConnId(connectorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transactionId]);

  // Fetch current transaction state on mount as baseline (polling fallback)
  useEffect(() => {
    if (!open || !transactionId) return;

    syncTransactionSnapshot();
  }, [open, transactionId, syncTransactionSnapshot]);

  useEffect(() => {
    if (!open || !transactionId) return;

    const intervalId = window.setInterval(() => {
      syncTransactionSnapshot();
    }, LIVE_STATUS_POLL_MS);

    return () => window.clearInterval(intervalId);
  }, [open, transactionId, syncTransactionSnapshot]);

  useEffect(() => {
    if (!open) {
      setSessionStartTime(null);
      setDuration(0);
    }
  }, [open]);

  // Update duration counter
  useEffect(() => {
    if (!open || !sessionStartTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - sessionStartTime.getTime()) / 60000);
      setDuration(diffMinutes);
    }, 1000);

    return () => clearInterval(interval);
  }, [open, sessionStartTime]);

  // Compute display values - only use real data from SSE
  // Check each metric independently: null/undefined means no data yet, 0 could be a real value only if we got a positive reading before
  const hasBatteryData = transactionData.currentSoc != null && transactionData.currentSoc > 0;
  const hasPowerData = transactionData.currentPowerKw != null && transactionData.currentPowerKw > 0;
  const hasEnergyData = transactionData.totalKwh != null && transactionData.totalKwh > 0;
  const displayBatteryLevel = hasBatteryData ? Number(transactionData.currentSoc) : 0;
  const displayEnergyConsumed = hasEnergyData ? Number(transactionData.totalKwh) : 0;
  const displayCurrentPower = hasPowerData ? Number(transactionData.currentPowerKw) : 0;
  // Session has real-time data when we've received any non-zero meter reading
  const isRealTimeData = hasBatteryData || hasPowerData;

  // Generate energy particles
  useEffect(() => {
    if (!open) return;

    const generateParticles = () => {
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: Date.now() + i,
        x: Math.random() * 100,
        delay: Math.random() * 2,
      }));
      setParticles(newParticles);
    };

    generateParticles();
    const interval = setInterval(generateParticles, 3000);

    return () => clearInterval(interval);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black flex flex-col pt-[env(safe-area-inset-top)]" style={{ zIndex: Z_INDEX.modal }}>
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, #8bc43e 1px, transparent 1px),
            linear-gradient(to bottom, #8bc43e 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}></div>
      </div>

      {/* Energy Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute bottom-0 w-2 h-2"
            style={{
              left: `${particle.x}%`,
              animation: `float-up 3s ease-in ${particle.delay}s infinite`,
            }}
          >
            <div className="w-full h-full bg-[#8bc43e] rounded-full opacity-60 blur-sm"></div>
          </div>
        ))}
      </div>

      {/* Scrollable Content Area */}
      <div className="relative z-10 flex-1 overflow-auto">
        <div className="p-6 pb-32">{/* Extra padding at bottom for sticky buttons */}

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-white font-bold text-2xl">Charging Active</h1>
              <div className={`text-sm flex items-center gap-2 mt-1 ${isRealTimeData ? 'text-green-400' : 'text-yellow-400'}`}>
                <div className={`w-2 h-2 rounded-full animate-pulse ${isRealTimeData ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
                {isRealTimeData ? 'Live Session' : 'Waiting for meter data'}
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-all"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Battery Visualization */}
          <div className="relative mb-6">
            {/* Circular Progress Ring - Reduced Size */}
            <div className="relative mx-auto w-44 h-44 mb-4">
              <svg className="w-full h-full transform -rotate-90">
                {/* Background circle */}
                <circle
                  cx="88"
                  cy="88"
                  r="80"
                  fill="none"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="10"
                />
                {/* Progress circle */}
                <circle
                  cx="88"
                  cy="88"
                  r="80"
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 80}`}
                  strokeDashoffset={`${2 * Math.PI * 80 * (hasBatteryData ? (1 - displayBatteryLevel / 100) : 1)}`}
                  className="transition-all duration-1000 ease-out"
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#8bc43e" />
                    <stop offset="100%" stopColor="#6fa02e" />
                  </linearGradient>
                </defs>
              </svg>

              {/* Center Content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {/* Battery Percentage */}
                <div className="text-4xl font-bold text-white mb-1">
                  {hasBatteryData ? `${displayBatteryLevel.toFixed(0)}%` : '--'}
                </div>
                <p className="text-green-400 text-xs font-medium">Battery</p>

                {/* Charging Animation Ring */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-[#8bc43e] rounded-full opacity-20 animate-ping"></div>
                </div>
              </div>
            </div>

            {/* Energy Flow Visualization */}
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#8bc43e] animate-pulse" fill="#8bc43e" />
                <span className="text-white text-sm">Energy Flowing</span>
              </div>
            </div>
          </div>

          {/* Primary Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Energy Consumed */}
            <div className={`bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 ${!isRealTimeData ? 'animate-pulse' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-[#8bc43e]" />
                <p className="text-gray-300 text-sm">Energy Used</p>
              </div>
              <p className="text-3xl font-bold text-white">{hasEnergyData ? displayEnergyConsumed.toFixed(2) : '--'}</p>
              <p className="text-gray-400 text-sm mt-1">kWh</p>
            </div>

            {/* Power */}
            <div className={`bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 ${!isRealTimeData ? 'animate-pulse' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <p className="text-gray-300 text-sm">Charging Speed</p>
              </div>
              <p className="text-3xl font-bold text-white">{hasPowerData ? displayCurrentPower.toFixed(2) : '--'}</p>
              <p className="text-gray-400 text-sm mt-1">kW</p>
            </div>
          </div>

          {/* Connector Status */}
          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  connectorRawStatus
                    ? getConnectorStatusColor(connectorRawStatus).dot
                    : 'bg-gray-500'
                } animate-pulse`}></div>
                <span className="text-sm text-gray-300">Connector Status</span>
              </div>
              <span className={`text-sm font-semibold ${
                connectorRawStatus
                  ? getConnectorStatusColor(connectorRawStatus).text
                  : 'text-gray-400'
              }`}>
                {connectorRawStatus ? formatConnectorStatus(connectorRawStatus) : 'Unknown'}
              </span>
            </div>
          </div>

          {/* Tariff (only shown when the session has a tariff) */}
          {transactionData.tariffs && transactionData.tariffs.length > 0 && (
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10 mb-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-gray-400" />
                Tariff
              </h3>

              <div className="space-y-3">
                {transactionData.tariffs.map((tariff, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">{tariff.name}</span>
                    <span className="text-sm font-semibold text-white">
                      {tariff.rate} {tariff.unit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Session Details */}
          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10 mb-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400" />
              Session Details
            </h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Duration</span>
                </div>
                <span className="text-sm font-semibold text-white">{formatDuration(duration)}</span>
              </div>

              {transactionData.meterStart !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Meter Start</span>
                  <span className="text-sm font-semibold text-white">{Number(transactionData.meterStart).toFixed(1)} kWh</span>
                </div>
              )}

              <div className="h-px bg-white/10"></div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Transaction ID</span>
                <span className="text-sm font-mono font-semibold text-[#8bc43e]">
                  {transactionId ? `#${transactionId.slice(0, 8)}` : '--'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Started</span>
                </div>
                <span className="text-xs text-gray-400">
                  {sessionStartTime ? sessionStartTime.toLocaleString() : '--'}
                </span>
              </div>

              {transactionData.lastUpdatedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Last Updated</span>
                  <span className="text-xs text-gray-400">
                    {new Date(transactionData.lastUpdatedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Auto-update Indicator */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className={`w-2 h-2 rounded-full animate-pulse ${isRealTimeData ? 'bg-green-400' : 'bg-yellow-400'}`}></div>
            <span className="text-xs text-gray-500">
              {isRealTimeData ? 'Live updates active' : 'Waiting for charging data...'}
            </span>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Button Bar */}
      <div className="relative z-20 border-t border-white/10 bg-gradient-to-t from-gray-900 via-gray-900/95 to-gray-900/80 backdrop-blur-xl">
        <div className="p-4 pb-6">
          <div className="grid grid-cols-2 gap-3">
            <Button
              className="h-14 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold border-2 border-white/20 backdrop-blur-md shadow-xl"
              onClick={() => window.location.href = SUPPORT_TEL}
              disabled={isStoppingCharge}
            >
              <Phone className="w-5 h-5 mr-2" />
              Support
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  className="h-14 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-xl disabled:opacity-50"
                  disabled={isStoppingCharge}
                >
                  {isStoppingCharge ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Stopping...
                    </>
                  ) : (
                    'Stop Charging'
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-gray-900 border-gray-700 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Stop Charging?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-400">
                    Are you sure you want to stop the current charging session? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-gray-700 hover:bg-gray-600 text-white border-gray-600">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={onStopCharging}
                  >
                    Stop Charging
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float-up {
          0% {
            transform: translateY(0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(-100vh) scale(0.5);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
